import { z } from 'zod';
import type { TFunction } from 'i18next';

const NAME_MAX_LENGTH = 200;
const BUSINESS_SITUATION_MAX_LENGTH = 400;

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
export const buildNacbIntakeFormSchema = (t: TFunction<'validation'>) =>
  z.object({
    name: z
      .string()
      .min(1, t('nacbIntake.intakeForm.nameIsRequired'))
      .max(NAME_MAX_LENGTH, t('nacbIntake.intakeForm.maxCharacters', { max: NAME_MAX_LENGTH })),
    brand: z
      .enum([
        '',
        'OFF!',
        'RAID',
        'STEM',
        'Glade',
        'Ziploc',
        'Pledge',
        'Scrubbing Bubbles',
        'Windex',
        'Shout',
        'Drano',
        'Other',
      ])
      .refine((value) => value !== '', { message: t('nacbIntake.intakeForm.brandIsRequired') }),
    businessSituation: z
      .string()
      .min(1, t('nacbIntake.intakeForm.businessSituationIsRequired'))
      .max(
        BUSINESS_SITUATION_MAX_LENGTH,
        t('nacbIntake.intakeForm.maxCharacters', { max: BUSINESS_SITUATION_MAX_LENGTH }),
      ),
    turnaroundTimeframe: z
      .enum(['', 'Fast (1–2 day turnaround)', 'Opportunistic (~1 week)', 'Not urgent (1–2 months)'])
      .refine((value) => value !== '', {
        message: t('nacbIntake.intakeForm.turnaroundTimeframeIsRequired'),
      }),
    involvementLevel: z.enum(['', 'High', 'Medium', 'Low']).refine((value) => value !== '', {
      message: t('nacbIntake.intakeForm.involvementLevelIsRequired'),
    }),
  });

export type NACBIntakeFormData = z.infer<ReturnType<typeof buildNacbIntakeFormSchema>>;
export type NACBIntakeFormInput = z.input<ReturnType<typeof buildNacbIntakeFormSchema>>;

export const defaultFormValues: NACBIntakeFormInput = {
  name: '',
  brand: '',
  businessSituation: '',
  turnaroundTimeframe: '',
  involvementLevel: '',
};

export function mapFormDataToDto(data: NACBIntakeFormData) {
  return {
    name: data.name,
    brand: data.brand,
    businessSituation: data.businessSituation,
    turnaroundTimeframe: data.turnaroundTimeframe,
    involvementLevel: data.involvementLevel,
  };
}
